
































































import Vue, { PropType } from 'vue';

interface GateState {
  kind: string | null;
  sub: string;
  pulseSeq: number;
  behind: boolean;
  busy: string;
}

interface Presentation {
  tone: string;
  icon: string;
  primary: string;
  secondary: string;
  i18nKey: string;
}

const EMPTY: GateState = {
  kind: null,
  sub: '',
  pulseSeq: 0,
  behind: false,
  busy: '',
};

const FATAL: { [kind: string]: boolean } = {
  MIXED_CONTENT_BLOCKED: true,
  AUTH_STORAGE_CORRUPT: true,
  SESSION_EXPIRED: true,
};

const PRESENTATION: { [kind: string]: Presentation } = {
  SESSION_EXPIRED: {
    tone: 'danger', icon: 'lock', primary: 'reloginButton', secondary: '', i18nKey: 'expired',
  },
  AUTH_STORAGE_CORRUPT: {
    tone: 'danger', icon: 'exclamation-circle', primary: 'reloginButton', secondary: '', i18nKey: 'corrupt',
  },
  MIXED_CONTENT_BLOCKED: {
    tone: 'danger', icon: 'api', primary: '', secondary: 'reloadButton', i18nKey: 'mixed',
  },
  BACKEND_UNREACHABLE: {
    tone: 'warning', icon: 'cloud', primary: 'retryButton', secondary: 'reloadButton', i18nKey: 'unreachable',
  },
  OFFLINE: {
    tone: 'info', icon: 'info-circle', primary: 'retryButton', secondary: '', i18nKey: 'offline',
  },
};

export default Vue.extend({
  name: 'SessionGate',

  props: {
    state: {
      type: Object as PropType<GateState | null>,
      default: null,
    },
  },

  data() {
    return {
      focusTaken: false,
    };
  },

  computed: {
    gate(): GateState {
      const s = this.state as GateState | null;
      return s ? s : EMPTY;
    },
    fatal(): boolean {
      const kind = this.gate.kind;
      return !!(kind && FATAL[kind]);
    },
    presentation(): Presentation {
      const p = PRESENTATION[this.gate.kind ? this.gate.kind : ''];
      return p ? p : PRESENTATION.BACKEND_UNREACHABLE;
    },
    tone(): string { return this.presentation.tone; },
    icon(): string { return this.presentation.icon; },
    primaryAction(): string { return this.presentation.primary; },
    secondaryAction(): string { return this.presentation.secondary; },
    i18nKey(): string {
      if (this.gate.kind === 'SESSION_EXPIRED' && this.gate.sub === 'LOGGED_OUT_ELSEWHERE') {
        return 'loggedOut';
      }
      if (this.gate.kind === 'BACKEND_UNREACHABLE' && this.gate.sub === 'TIMEOUT') {
        return 'timeout';
      }
      return this.presentation.i18nKey;
    },
    title(): string {
      return this.t('auth.session.' + this.i18nKey + 'Title');
    },
    text(): string {
      return this.t('auth.session.' + this.i18nKey + 'Text');
    },
    announcement(): string {
      return this.title + ' ' + this.text;
    },
    pulseCounter(): number { return this.gate.pulseSeq; },
    kindKey(): string { return String(this.gate.kind); },
  },

  watch: {
    pulseCounter(): void {
      this.$nextTick(() => { this.shake(); this.takeFocusOnce(); });
    },
    kindKey(): void {
      this.focusTaken = false;
      this.$nextTick(() => { this.takeFocusOnce(); });
    },
  },

  mounted(): void {
    document.addEventListener('keydown', this.onKeyDown, false);

    // на момент mounted корень ещё не в документе, без nextTick focus() ничего не сделает
    this.$nextTick(() => { this.takeFocusOnce(); });

    const listeners = this.$listeners;
    if (!listeners.relogin && !listeners.reload && !listeners.retry) {
      console.error(
        '[SessionGate] карточка смонтирована без слушателей relogin/reload/retry — кнопки не сработают.',
      );
    }
  },

  beforeDestroy(): void {
    document.removeEventListener('keydown', this.onKeyDown, false);
  },

  methods: {
    t(key: string, values?: { [k: string]: any }): string {
      const self = this as any;
      if (typeof self.$t !== 'function') { return key; }
      return String(values ? self.$t(key, values) : self.$t(key));
    },

    actionTestId(action: string): string {
      const id = String(action).replace(/Button$/, '').toLowerCase();
      return id ? id : 'action';
    },

    /* класс ставится через classList: :class-биндинг на этом узле Vue затрёт при перерисовке.
       Чтение offsetWidth форсирует reflow, иначе remove+add класса не перезапустят анимацию */
    shake(): void {
      const el = this.$refs.shake as HTMLElement | undefined;
      if (!el) { return; }
      el.classList.remove('sp-gate__shake--pulse');
      // tslint:disable-next-line:no-unused-expression
      void el.offsetWidth;
      el.classList.add('sp-gate__shake--pulse');
    },

    takeFocusOnce(): void {
      if (this.focusTaken || !this.fatal) { return; }
      const card = this.$refs.card as HTMLElement | undefined;
      if (!card || typeof card.focus !== 'function') { return; }

      const active = document.activeElement as HTMLElement | null;
      const root = this.$el as HTMLElement | undefined;
      if (active && root && root.contains(active)) { this.focusTaken = true; return; }
      if (this.isEditable(active)) { return; }

      card.focus();
      this.focusTaken = true;
    },

    /* предел в восемь шагов вверх: глубже редактируемого контейнера не бывает */
    isEditable(el: HTMLElement | null): boolean {
      let node: any = el;
      let depth = 0;
      while (node && depth < 8) {
        const tag = String(node.tagName);
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') { return true; }
        if (node.isContentEditable === true) { return true; }
        node = node.parentElement;
        depth += 1;
      }
      return false;
    },

    onShakeEnd(ev: Event): void {
      const el = this.$refs.shake as HTMLElement | undefined;
      if (!el) { return; }
      // снимаем класс только по своей анимации: animationend волны ant-кнопки всплывает сюда
      const target = ev ? (ev.target as HTMLElement | null) : null;
      const own = target === el
        || (!!target && !!target.classList && target.classList.contains('sp-gate'));
      if (!own) { return; }
      el.classList.remove('sp-gate__shake--pulse');
    },

    onKeyDown(ev: KeyboardEvent): void {
      if (ev.repeat || !this.gate.kind) { return; }
      const k = ev.key;
      if (k !== 'Escape' && k !== 'Esc') { return; }
      const root = this.$el as HTMLElement;
      if (root && root.contains(ev.target as Node)) { this.shake(); }
    },

    onPrimary(): void { this.runAction(this.primaryAction); },
    onSecondary(): void { this.runAction(this.secondaryAction); },

    runAction(action: string): void {
      if (!action || this.gate.busy) { return; }
      if (action === 'reloginButton') { this.$emit('relogin'); return; }
      if (action === 'reloadButton') { this.$emit('reload'); return; }
      if (action === 'retryButton') { this.$emit('retry'); return; }
    },
  },
});
